import { z } from 'zod';
import {
  MIDDLEWARE_ID_PATTERN,
  middlewareIdSchema,
  type MiddlewareId,
  type MiddlewareModuleId,
} from './middlewares';
import { MANIFEST_MODULE_ID_PATTERN } from './modules';
import { ODOO_MODULE_SETTINGS, ODOO_SETTINGS } from './odoo-settings';

/**
 * Middleware settings as field descriptors (plan §6). The Tenant Admin enters every value in
 * the app; secrets are write-only (the API returns `{ isSet }` only) and are sealed on the
 * school. Central sees only `configured: boolean`.
 */

export const settingsFieldTypeSchema = z.enum(['text', 'url', 'secret', 'select', 'timezone']);
export type SettingsFieldType = z.infer<typeof settingsFieldTypeSchema>;

export const SETTINGS_KEY_PATTERN = /^[a-z][A-Za-z0-9]{0,39}$/;
export const SETTINGS_VALUE_MAX = 2000;

export const settingsFieldSchema = z.object({
  key: z.string().regex(SETTINGS_KEY_PATTERN),
  type: settingsFieldTypeSchema,
  label: z.string().min(1).max(80),
  help: z.string().max(300).optional(),
  required: z.boolean(),
  /** Used when the school has not saved a value. Never set on a secret. */
  default: z.string().max(SETTINGS_VALUE_MAX).optional(),
  /** Choices for a `select` field. */
  options: z
    .array(z.object({ value: z.string().min(1).max(100), label: z.string().min(1).max(80) }))
    .optional(),
  /**
   * `secret` only: the Tenant Admin may copy the stored value to the clipboard (never shown on
   * screen; audited). For credentials an admin must be able to check against the vendor's.
   */
  copyable: z.boolean().optional(),
  /**
   * Non-secret fields: a non-blank value must match `regex` (a RegExp source), else `message`
   * (additive, 2026-10-26; e.g. Odoo field names before they reach a query).
   */
  pattern: z
    .object({ regex: z.string().min(1).max(200), message: z.string().min(1).max(120) })
    .optional(),
  /**
   * Optional plain fields (additive, 2026-10-26): a value saved blank is kept as blank and
   * switches that part off, instead of falling back to `default`. Never saved = the default.
   */
  emptyMeansOff: z.boolean().optional(),
});
export type SettingsField = z.infer<typeof settingsFieldSchema>;

/** PowerSchool connector settings (shared by its modules). No per-user overrides. */
export const POWERSCHOOL_SETTINGS: readonly SettingsField[] = [
  {
    key: 'baseUrl',
    type: 'url',
    label: 'PowerSchool server',
    help: 'For example https://school.powerschool.com',
    required: true,
  },
  {
    key: 'clientId',
    // Write-only like the secret: the pair is the school's PowerSchool API credential.
    type: 'secret',
    copyable: true,
    label: 'Client ID',
    help: 'Plugin Management › Mantle - TeachPartner › Data Configuration',
    required: true,
  },
  {
    key: 'clientSecret',
    type: 'secret',
    copyable: true,
    label: 'Client secret',
    help: 'Plugin Management › Mantle - TeachPartner › Data Configuration',
    required: true,
  },
  {
    key: 'schoolTimezone',
    type: 'timezone',
    label: 'School time zone',
    help: 'Decides which day is "today" on the timetable',
    required: true,
    default: 'Asia/Bahrain',
  },
];

/** Settings descriptor per catalogue middleware (connection and middleware-wide settings). */
export const MIDDLEWARE_SETTINGS: Readonly<Record<MiddlewareId, readonly SettingsField[]>> = {
  powerschool: POWERSCHOOL_SETTINGS,
  odoo: ODOO_SETTINGS,
};

/** PowerSchool · Timetable module settings (Admin › Modules › ⚙). */
export const TIMETABLE_MODULE_SETTINGS: readonly SettingsField[] = [
  {
    key: 'periods',
    type: 'text',
    label: 'Bell periods',
    help: "The school's full bell list, comma-separated, in order",
    required: true,
    default: 'HR,P1,P2,P3,P4,P5,P6',
  },
];

/**
 * Module settings descriptors (2026-10-11), keyed by module id. Plain values only: a `secret`
 * belongs on the middleware. A module without an entry has no settings screen.
 */
export const MODULE_SETTINGS: Readonly<
  Partial<Record<MiddlewareModuleId, readonly SettingsField[]>>
> = {
  'powerschool.timetable': TIMETABLE_MODULE_SETTINGS,
  ...ODOO_MODULE_SETTINGS,
};

export function moduleSettingsFor(moduleId: string): readonly SettingsField[] {
  return MODULE_SETTINGS[moduleId as MiddlewareModuleId] ?? [];
}

/** GET/PUT /api/v1/admin/modules/:moduleId/settings (modules.manage). */
export const moduleIdParamsSchema = z.object({
  moduleId: z.string().regex(MANIFEST_MODULE_ID_PATTERN),
});
export const MODULE_SETTINGS_PATH = '/api/v1/admin/modules/:moduleId/settings';

export const middlewareIdParamsSchema = z.object({ middlewareId: middlewareIdSchema });

/** POST /api/v1/admin/middlewares/:middlewareId/secrets/:key/copy (Tenant Admin only). */
export const middlewareSecretParamsSchema = z.object({
  middlewareId: middlewareIdSchema,
  key: z.string().regex(SETTINGS_KEY_PATTERN),
});
/** The opened secret, for the clipboard only: never rendered, cached (no-store) or logged. */
export const middlewareSecretCopyResponseSchema = z.object({
  value: z.string().min(1).max(SETTINGS_VALUE_MAX),
});
export type MiddlewareSecretCopyResponse = z.infer<typeof middlewareSecretCopyResponseSchema>;
export const MIDDLEWARE_SECRET_COPY_PATH =
  '/api/v1/admin/middlewares/:middlewareId/secrets/:key/copy';

const settingsValuesSchema = z.record(
  z.string().regex(SETTINGS_KEY_PATTERN),
  z.string().max(SETTINGS_VALUE_MAX),
);

/** GET (and PUT reply) /api/v1/admin/middlewares/:middlewareId/settings. Secrets never leave. */
export const middlewareSettingsResponseSchema = z.object({
  middlewareId: z.string().regex(MIDDLEWARE_ID_PATTERN),
  fields: z.array(settingsFieldSchema),
  /** Non-secret values (saved, else the field default). */
  values: z.record(z.string(), z.string()),
  secrets: z.record(z.string(), z.object({ isSet: z.boolean() })),
  /** Every required field has a value (secrets: isSet). */
  configured: z.boolean(),
});
export type MiddlewareSettingsResponse = z.infer<typeof middlewareSettingsResponseSchema>;

/**
 * PUT /api/v1/admin/middlewares/:middlewareId/settings. Omitted keys are unchanged; an empty
 * string secret clears it. The server rejects keys that aren't in the descriptor.
 */
export const middlewareSettingsUpdateSchema = z.object({
  values: settingsValuesSchema.optional(),
  secrets: settingsValuesSchema.optional(),
});
export type MiddlewareSettingsUpdate = z.infer<typeof middlewareSettingsUpdateSchema>;

/**
 * POST /api/v1/admin/middlewares/:middlewareId/test ("Test connection"). Optional body: the
 * form's unsaved draft (same shape and rules as PUT), tested over the stored settings without
 * saving. No body tests what is saved.
 */
export const middlewareTestRequestSchema = middlewareSettingsUpdateSchema.nullish();
export type MiddlewareTestRequest = z.infer<typeof middlewareTestRequestSchema>;
/** One fact the connection test learned, e.g. { label: 'PowerSchool version', value: '26.7' }. */
export const middlewareTestDetailSchema = z.object({
  label: z.string().min(1).max(60),
  value: z.string().min(1).max(200),
});
export type MiddlewareTestDetail = z.infer<typeof middlewareTestDetailSchema>;
export const MIDDLEWARE_TEST_DETAILS_MAX = 10;

export const middlewareTestResponseSchema = z.object({
  ok: z.boolean(),
  message: z.string().min(1).max(500),
  /** Optional facts to show under the message (additive; older servers leave it out). */
  details: z.array(middlewareTestDetailSchema).max(MIDDLEWARE_TEST_DETAILS_MAX).optional(),
  /**
   * Values the vendor reported for non-secret settings, already validated against the field
   * (e.g. PowerSchool's district time zone for `schoolTimezone`). The app may fill them in;
   * nothing is saved.
   */
  suggestedValues: z
    .record(z.string().regex(SETTINGS_KEY_PATTERN), z.string().min(1).max(SETTINGS_VALUE_MAX))
    .optional(),
});
export type MiddlewareTestResponse = z.infer<typeof middlewareTestResponseSchema>;

/** GET /api/v1/admin/middlewares: every catalogue middleware with its allow and module state. */
export const adminMiddlewaresResponseSchema = z.object({
  middlewares: z.array(
    z.object({
      id: z.string().regex(MIDDLEWARE_ID_PATTERN),
      title: z.string().min(1).max(60),
      /** Allowed by the Host Admin for this school. */
      allowed: z.boolean(),
      configured: z.boolean(),
      modules: z.array(
        z.object({
          /** Dotted id, e.g. `powerschool.timetable`. */
          id: z.string().min(1),
          title: z.string().min(1).max(60),
          enabled: z.boolean(),
        }),
      ),
    }),
  ),
});
export type AdminMiddlewaresResponse = z.infer<typeof adminMiddlewaresResponseSchema>;

/** GET (and PUT reply) module settings: plain values with defaults; `configured` = all required set. */
export const moduleSettingsResponseSchema = z.object({
  moduleId: z.string().regex(MANIFEST_MODULE_ID_PATTERN),
  fields: z.array(settingsFieldSchema),
  values: z.record(z.string(), z.string()),
  configured: z.boolean(),
});
export type ModuleSettingsResponse = z.infer<typeof moduleSettingsResponseSchema>;

/** PUT body: omitted keys unchanged; a blank value falls back to the default. */
export const moduleSettingsUpdateSchema = z.strictObject({ values: settingsValuesSchema });
export type ModuleSettingsUpdate = z.infer<typeof moduleSettingsUpdateSchema>;
